import { Routes, Route } from 'react-router-dom';
import { useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { usePageView } from '@koob/google-tag-manager';
import { AuthContext } from './contexts';
import AppProvider from 'providers/AppProvider';
import RoutesProvider from 'providers/RoutesProvider';
import { lazy } from 'react';
import { Navigate } from 'react-router';
import Folders from './containers/Folders';
import BookingProvider from './providers/BookingProvider';
import ExperienceBookingProvider from './providers/ExperienceBookingProvider';
import LanguageProvider from 'providers/LanguageProvider';
import { NotificationsProvider } from '@koob/notifications';
import * as notificationsApi from 'api/notifications';
import TripDesignerConfigProvider from './providers/TripDesignerConfigProvider';
import TripDesignerRoutes from './containers/TripDesigner';
// KD-3735: one-organization pages ask which organization in the group view.
import RequireActingOrganization from 'components/RequireActingOrganization';
import NotFound from './containers/NotFound';
import TripsRoutes from './containers/Trips';
import ProfileContainer from './containers/Profile';
import { KoobMapsProvider } from '@koob/maps';
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_MAP_ID } from './constants';

const Organizations = lazy(() => import('containers/Organizations'));
const TravelDiarySettings = lazy(
  () => import('containers/TravelDiarySettings'),
);
const TravelDiaryContentLibrary = lazy(
  () => import('containers/TravelDiaryContentLibrary'),
);
const Users = lazy(() => import('containers/Users'));
const Hotels = lazy(() => import('containers/Hotels'));
const Bookings = lazy(() => import('containers/Bookings'));
const Dmcs = lazy(() => import('containers/Dmcs'));
const Experiences = lazy(() => import('containers/Experiences'));
const McpAuthorize = lazy(() => import('containers/McpAuthorize'));
const AuditLog = lazy(() => import('containers/AuditLog'));

const MapsWrapper = ({ children }) => {
  return (
    <KoobMapsProvider apiKey={GOOGLE_MAPS_API_KEY} mapId={GOOGLE_MAPS_MAP_ID}>
      {children}
    </KoobMapsProvider>
  );
};

const App = () => {
  const { accessToken, logout, isActiveToken } = useContext(AuthContext);
  const location = useLocation();
  usePageView(location);

  if (!Boolean(accessToken)) {
    // Carry the target so Login can come back (deep links, MCP consent).
    return (
      <Navigate
        to="/login"
        state={{ from: location.pathname + location.search }}
      />
    );
  }
  if (!isActiveToken) {
    logout();
  }

  return (
    <MapsWrapper>
      <RoutesProvider>
        <AppProvider>
          <BookingProvider>
            <ExperienceBookingProvider>
              <LanguageProvider>
                <TripDesignerConfigProvider>
                  <NotificationsProvider api={notificationsApi}>
                    <Routes>
                      <Route
                        path="/"
                        element={<Navigate to="/folders" replace />}
                      />
                      <Route
                        path="hotels/*"
                        element={
                          <RequireActingOrganization>
                            <Hotels />
                          </RequireActingOrganization>
                        }
                      />
                      <Route
                        path="experiences/*"
                        element={
                          <RequireActingOrganization>
                            <Experiences />
                          </RequireActingOrganization>
                        }
                      />
                      <Route path="bookings/*" element={<Bookings />} />
                      <Route path="users/*" element={<Users />} />
                      <Route
                        path="organizations/*"
                        element={<Organizations />}
                      />
                      <Route
                        path="travel-diary-content-library/*"
                        element={<TravelDiaryContentLibrary />}
                      />
                      <Route
                        path="travel-diary-settings"
                        element={
                          <RequireActingOrganization>
                            <TravelDiarySettings />
                          </RequireActingOrganization>
                        }
                      />
                      <Route
                        path="dmcs/*"
                        element={
                          <RequireActingOrganization>
                            <Dmcs />
                          </RequireActingOrganization>
                        }
                      />
                      <Route path="folders/*" element={<Folders />} />
                      <Route
                        path="trip-designer/*"
                        element={
                          <RequireActingOrganization>
                            <TripDesignerRoutes />
                          </RequireActingOrganization>
                        }
                      />
                      <Route path="trips/*" element={<TripsRoutes />} />
                      <Route path="user/*" element={<ProfileContainer />} />
                      <Route path="audit-log" element={<AuditLog />} />
                      <Route path="mcp/authorize" element={<McpAuthorize />} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </NotificationsProvider>
                </TripDesignerConfigProvider>
              </LanguageProvider>
            </ExperienceBookingProvider>
          </BookingProvider>
        </AppProvider>
      </RoutesProvider>
    </MapsWrapper>
  );
};

export default App;
